import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Typography, Button } from "@altimateai/lego";
import { Stack, Spinner } from "@altimateai/lego";
import {
  SubscriptionDetailHeader,
  DescriptionSection,
  HistorySection,
  useSubscriptionDetail,
} from "./components/details";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { AlertsTracking } from "@/lib/tracking/alerts";

const SubscriptionDetailPage = () => {
  const { id } = useParams();

  useEffect(() => {
    if (id) AlertsTracking.ruleDetailViewed(id);
  }, [id]);

  const {
    enrichedSubscription,
    historyData,
    isLoading,
    isError,
    historyError,
    isHistoryLoading,
    statusFilter,
    currentPeriod,
    page,
    pageSize,
    numPages,
    handleStatusFilterChange,
    handlePeriodSelect,
    setPage,
    setPageSize,
    resetPage,
  } = useSubscriptionDetail(id);

  useBreadcrumb(enrichedSubscription?.name ?? "Loading...", {
    defaultParents: [
      { label: "Settings", pathname: "/settings", noLink: true },
      { label: "Subscriptions", pathname: "/subscriptions" },
    ],
  });

  // Loading state
  if (isLoading) {
    return (
      <Stack direction="column" align="center" justify="center" className="al-h-screen">
        <Spinner />
      </Stack>
    );
  }

  // Error state
  if (isError || !enrichedSubscription || !id) {
    return (
      <Stack direction="column" align="center" justify="center" className="al-h-screen">
        <Typography variant="body" className="al-text-red-500">
          Failed to load subscription details
        </Typography>
        <Button asChild className="al-mt-4">
          <Link to="/subscriptions">Back to Subscriptions</Link>
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction="column" className="al-h-screen">
      <SubscriptionDetailHeader
        name={enrichedSubscription.name}
        subscriptionId={id}
        readOnly={enrichedSubscription.read_only}
      />

      <Stack direction="column" flex="1" className="al-overflow-y-auto al-mt-4">
        <Stack direction="column" gap="2xl">
          <DescriptionSection
            prompt={enrichedSubscription.prompt}
            description={enrichedSubscription.description}
            variables={enrichedSubscription.variables}
            readOnly={enrichedSubscription.read_only}
          />

          <HistorySection
            statusFilter={statusFilter}
            currentPeriod={currentPeriod}
            subscriptionType={enrichedSubscription.rule_type}
            historyData={historyData}
            historyError={historyError}
            isHistoryLoading={isHistoryLoading}
            page={page}
            pageSize={pageSize}
            numPages={numPages}
            onStatusFilterChange={handleStatusFilterChange}
            onPeriodChange={handlePeriodSelect}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onResetPage={resetPage}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SubscriptionDetailPage;
